import React, { useCallback, useEffect, useState } from "react";
import "./index.scss";
import { Modal, Row, Col } from "reactstrap";
import CustomTextInput from "components/CustomTextInput";
import { Button } from "shared";
import moment from "moment";
import ApiCaller from "api/apiCaller";
import PaymentScheduleTable from "./PaymentScheduleTable";
import { LISA } from "types";

type ModalPaymentScheduleProps = {
  isOpen: boolean;
  toggle: () => void;
  startDate: string;
  endDate: string;
  onRunPaymentSchedule: (data: {from_date: string; to_date: string}) => void;
  siteId: string;
};

const ModalPaymentSchedule = ({
  isOpen,
  toggle,
  startDate,
  endDate,
  siteId,
  onRunPaymentSchedule,
}: ModalPaymentScheduleProps) => {
  const [paymentSchedules, setPaymentSchedules] = useState<LISA.LeasePaymentScheduleApiData[]>([]);
  const [data, setData] = useState({
    from_date: moment(startDate || new Date())
      .startOf("month")
      .format("YYYY-MM-DD"),
    to_date: moment(endDate || new Date())
      .startOf("month")
      .format("YYYY-MM-DD"),
  });

  const updateAttribute = useCallback((att: string, value: any) => {
    setData(current => ({
      ...current,
      [att]: value,
    }));
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    if (isOpen) {
      ApiCaller.get(
        `site_lease_payment_schedule/site/${encodeURIComponent(siteId)}?from_date=${encodeURIComponent(data.from_date)}&to_date=${encodeURIComponent(data.to_date)}`,
        undefined,
        {
          signal: controller.signal,
        }
      )
      .then(resp => {
        setPaymentSchedules(resp.site_lease_payment_schedule || []);
      });
    }

    return () => {
      controller.abort();
    }
  }, [data.from_date, data.to_date, isOpen, siteId]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} style={{ maxWidth: 600 }}>
      <div className="payment-schedule__header">
        <span className="payment-schedule__title">Payment Schedule</span>
      </div>
      <div className="payment-schedule__body">
        <Row>
          <Col sm={{ size: 6, offset: 0 }}>
            <CustomTextInput
              type="date"
              callback={updateAttribute}
              data={data}
              labelText="From date"
              attributeName="from_date"
              showMonthYearOnly
            />
          </Col>
          <Col sm={{ size: 6, offset: 0 }}>
            <CustomTextInput
              type="date"
              callback={updateAttribute}
              data={data}
              labelText="To date"
              attributeName="to_date"
              showMonthYearOnly
            />
          </Col>
        </Row>
        {paymentSchedules.length > 0 && (
          <PaymentScheduleTable data={paymentSchedules} />
        )}
      </div>
      <div className="payment-schedule__footer">
        <Button label="Cancel" onClick={toggle} />
        <div style={{ width: 10 }} />
        <Button
          label="Run"
          type="primary"
          onClick={() => onRunPaymentSchedule(data)}
        />
      </div>
    </Modal>
  );
};

export default ModalPaymentSchedule;
